import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Modal, Form, Input, InputNumber, DatePicker, message, Spin, Select } from "antd";
import { PayerAxiosInstance } from "../../../Network/Payer";
import { Company } from "../../../interface/Company";
import dayjs from "dayjs";
import styles from "./CompanySelector.module.css";
import { COUNTRIES_CITIES, COUNTRY_LIST } from "../../../interface/countriesData";

// ─── icons ────────────────────────────────────────────────────────────────────
const IconSearch = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
  </svg>
);
const IconPlus = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <path d="M12 5v14M5 12h14"/>
  </svg>
);
const IconEdit = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);
const IconTrash = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
    <path d="M10 11v6M14 11v6"/>
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
  </svg>
);
const IconArrow = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M5 12h14M12 5l7 7-7 7"/>
  </svg>
);
const IconBuilding = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <rect x="3" y="3" width="18" height="18" rx="2"/>
    <path d="M9 3v18M15 3v18M3 9h18M3 15h18"/>
  </svg>
);

// ─── type ─────────────────────────────────────────────────────────────────────
type CompanyFull = Company & {
  active?: boolean;
  transactions?: number;
  endedAt?: string;
  numberOfEmployees?: number;
  bayanCompanyName?: string;
  bayanUserName?: string;
};

// ─── CompanyForm ──────────────────────────────────────────────────────────────
function CompanyForm({
  form, onFinish, submitting, onCancel, mode,
}: {
  form: any;
  onFinish: (v: any) => void;
  submitting: boolean;
  onCancel: () => void;
  mode: "add" | "edit";
}) {
  const [selectedCountry, setSelectedCountry] = useState<string>("");
  const cities = selectedCountry ? (COUNTRIES_CITIES[selectedCountry] ?? []) : [];

  useEffect(() => {
    const country = form.getFieldValue("country");
    if (country) setSelectedCountry(country);
  }, []);

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      className={styles.form}
      data-testid={`CompanyForm-${mode}-form`}
    >

      {/* row 2 – company name + employees */}
      <div className={styles.formRow}>
        <Form.Item label="Company Name" name="name" rules={[{ required: true, message: "Required" }]}>
          <Input data-testid={`CompanyForm-${mode}-name-input`} placeholder="Tech Corporation" />
        </Form.Item>
        <Form.Item
  label="Number of Employees"
  name="numberOfEmployees"
  rules={[
    { required: true, message: "Required" },
    {
      validator: (_, value) => {
        console.log("validating numberOfEmployees:", value);
        if (value === undefined || value === null) return Promise.resolve();
        if (value < 2) {
          return Promise.reject(new Error("Number of employees must be at least 2"));
        }
        return Promise.resolve();
      },
    },
  ]}
>
  <InputNumber
    data-testid={`CompanyForm-${mode}-employees-input`}
    min={0}
    max={255}
    style={{ width: "100%" }}
    placeholder="3"
  />
</Form.Item>
      </div>

      {/* row 3 – country + city */}
      <div className={styles.formRow}>
        <Form.Item label="Country" name="country" rules={[{ required: true, message: "Required" }]}>
          <Select
            data-testid={`CompanyForm-${mode}-country-select`}
            showSearch
            placeholder="Select country..."
            optionFilterProp="label"
            onChange={(val) => {
              setSelectedCountry(val);
              form.setFieldValue("city", undefined);
            }}
            options={COUNTRY_LIST.map(c => ({ value: c, label: c }))}
          />
        </Form.Item>
        <Form.Item label="City" name="city" rules={[{ required: true, message: "Required" }]}>
          <Select
            data-testid={`CompanyForm-${mode}-city-select`}
            showSearch
            placeholder={selectedCountry ? "Select city..." : "Choose country first"}
            disabled={!selectedCountry}
            optionFilterProp="label"
            options={cities.map(c => ({ value: c, label: c }))}
          />
        </Form.Item>
      </div>

      {/* row 4 – transactions + end date */}
      <div className={styles.formRow}>
        <Form.Item label="Transactions (optional)" name="transactions">
          <InputNumber
            data-testid={`CompanyForm-${mode}-transactions-input`}
            min={0}
            style={{ width: "100%" }}
            placeholder="Default: 1000"
          />
        </Form.Item>
        <Form.Item label="End Date (optional)" name="endedAt">
          <DatePicker
            data-testid={`CompanyForm-${mode}-endDate-picker`}
            style={{ width: "100%" }}
            placeholder="Default: 3 months"
          />
        </Form.Item>
      </div>

    {/* bayan – 3 fields in ONE row — Add mode only */}
    {mode === "add" && (
        <div className={styles.bayanSection}>
          <p className={styles.bayanTitle}>Bayan Integration</p>
          <div className={styles.formRow3}>
            <Form.Item label="Bayan Company Name" name="bayanCompanyName" rules={[{ required: true, message: "Required" }]}>
              <Input data-testid="CompanyForm-add-bayanCompanyName-input" placeholder="Kayan Provider" />
            </Form.Item>
            <Form.Item label="Bayan Username" name="bayanUserName" rules={[{ required: true, message: "Required" }]}>
              <Input data-testid="CompanyForm-add-bayanUserName-input" placeholder="kyan_provider" />
            </Form.Item>
            <Form.Item label="Bayan Password" name="bayanPassword" rules={[{ required: true, message: "Required" }]}>
              <Input.Password data-testid="CompanyForm-add-bayanPassword-input" placeholder="Bayan password" />
            </Form.Item>
          </div>
        </div>
      )}

      {/* footer */}
      <div className={styles.formFooter}>
        <button
          type="button"
          className={styles.btnCancel}
          data-testid={`CompanyForm-${mode}-cancel-button`}
          onClick={onCancel}
        >
          Cancel
        </button>
        <button
          type="submit"
          className={styles.btnSubmit}
          data-testid={`CompanyForm-${mode}-submit-button`}
          disabled={submitting}
        >
          {submitting && <span className={styles.spinner} />}
          {mode === "add" ? "Create Company" : "Save Changes"}
        </button>
      </div>
    </Form>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function CompanySelector() {
  const [companies, setCompanies]   = useState<CompanyFull[]>([]);
  const [filtered, setFiltered]     = useState<CompanyFull[]>([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState("");
  const [addOpen, setAddOpen]       = useState(false);
  const [editOpen, setEditOpen]     = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [target, setTarget]         = useState<CompanyFull | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting]     = useState(false);
  const [addForm]  = Form.useForm();
  const [editForm] = Form.useForm();
  const navigate   = useNavigate();

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const res = await PayerAxiosInstance.get("company", { params: { page: 1, limit: 100 } });
      const list: CompanyFull[] = res.data?.data?.items ?? res.data?.items ?? [];
      setCompanies(list);
      setFiltered(list);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchCompanies(); }, []);

  useEffect(() => {
    const q = search.toLowerCase();
    setFiltered(q
      ? companies.filter(c =>
          c.name?.toLowerCase().includes(q) ||
          c.username?.toLowerCase().includes(q) ||
          c.country?.toLowerCase().includes(q) ||
          c.city?.toLowerCase().includes(q))
      : companies
    );
  }, [search, companies]);

  const handleSelect = (company: CompanyFull) => {
    sessionStorage.setItem("selectedCompany", JSON.stringify({ id: company.id, name: company.name }));
    window.dispatchEvent(new Event("companyChanged"));
    navigate(`/dashboard?companyId=${company.id}`);
  };

  const handleAdd = async (values: any) => {
    setSubmitting(true);
    try {
      const payload: any = {
        name: values.name, country: values.country, city: values.city,
        bayanCompanyName: values.bayanCompanyName,
        bayanUserName: values.bayanUserName,
        bayanPassword: values.bayanPassword,
        numberOfEmployees: values.numberOfEmployees,
      };
      if (values.transactions != null) payload.transactions = values.transactions;
      if (values.endedAt) payload.endedAt = values.endedAt.format("YYYY-MM-DD");

      await PayerAxiosInstance.post("company", payload);
      message.success("Company created!");
      addForm.resetFields();
      setAddOpen(false);
      fetchCompanies();
    } catch (e: any) {
      const details = e?.response?.data?.error?.details;
      const detailMsg = Array.isArray(details) && details.length > 0
        ? details.map((d: any) => d.message).join(", ")
        : null;
      message.error(detailMsg || e?.response?.data?.error?.message || "Failed to update");
    } finally { setSubmitting(false); }
  };

  const handleEdit = async (values: any) => {
    if (!target) return;
    setSubmitting(true);
    try {
      const payload: any = {
        name: values.name,
        country: values.country,
        city: values.city,
        transactions: values.transactions ?? target.transactions ?? 1000,
        endedAt: values.endedAt
          ? values.endedAt.format("YYYY-MM-DD")
          : target.endedAt ?? new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
        active: target.active ?? true,
        numberOfEmployees: values.numberOfEmployees,
        bayanCompanyName: values.bayanCompanyName,
        bayanUserName: values.bayanUserName,
        bayanPassword: values.bayanPassword,
      };
      console.log(payload)
      if (values.password) payload.password = values.password;
      await PayerAxiosInstance.put(`company/${target.id}`, payload);
      message.success("Company updated!");
      setEditOpen(false);
      fetchCompanies();
    } catch (e: any) {
      const details = e?.response?.data?.error?.details;
      const detailMsg = Array.isArray(details) && details.length > 0
        ? details.map((d: any) => d.message).join(", ")
        : null;
      message.error(detailMsg || e?.response?.data?.error?.message || "Failed to update");
    } finally { setSubmitting(false); }
  };

  const handleDelete = async () => {
    if (!target) return;
    setDeleting(true);
    try {
      await PayerAxiosInstance.delete(`company/${target.id}`);
      message.success("Company deleted.");
      setDeleteOpen(false);
      setTarget(null);
      fetchCompanies();
    } catch (e: any) {
      message.error(e?.response?.data?.error?.message || "Failed to delete");
    } finally { setDeleting(false); }
  };

  const openEdit = (c: CompanyFull) => {
    setTarget(c);
    editForm.setFieldsValue({
      name: c.name,
      country: c.country,
      city: c.city,
      numberOfEmployees: c.numberOfEmployees,
      transactions: c.transactions,
      endedAt: c.endedAt ? dayjs(c.endedAt) : undefined,
      bayanCompanyName: c.bayanCompanyName ?? "",
      bayanUserName: c.bayanUserName ?? "",
    });
    setEditOpen(true);
  };

  return (
    <div className={styles.page} data-testid="CompanySelector-page">

      {/* ── top bar ── */}
      <div className={styles.topbar}>
        <div>
          <h1 className={styles.pageTitle}>Companies</h1>
          <p className={styles.pageSub}>{companies.length} companies registered</p>
        </div>
        <div className={styles.topbarRight}>
          <div className={styles.searchWrap}>
            <IconSearch />
            <input
              data-testid="CompanySelector-search-input"
              className={styles.searchInput}
              placeholder="Search companies..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <button
            data-testid="CompanySelector-addCompany-button"
            className={styles.addBtn}
            onClick={() => setAddOpen(true)}
          >
            <IconPlus /> Add Company
          </button>
        </div>
      </div>

      {/* ── grid ── */}
      <div className={styles.grid} data-testid="CompanySelector-grid">
        {loading ? (
          <div className={styles.loadingWrap} data-testid="CompanySelector-loading">
            <Spin size="large" />
          </div>
        ) : filtered.length === 0 ? (
          <div className={styles.empty} data-testid="CompanySelector-empty">
            {search ? `No results for "${search}"` : "No companies yet. Click Add Company to get started."}
          </div>
        ) : filtered.map(company => (
          <div key={company.id} className={styles.card} data-testid={`CompanySelector-card-${company.id}`}>

            {/* card header */}
            <div className={styles.cardHead}>
              <div className={styles.cardLeft}>
                <div className={styles.cardIcon}><IconBuilding /></div>
                <div>
                  <p className={styles.cardName}>{company.name}</p>
                  <p className={styles.cardUser}>@{company.username}</p>
                </div>
              </div>
              <div className={styles.cardActions} onClick={e => e.stopPropagation()}>
                <button
                  data-testid={`CompanySelector-edit-button-${company.id}`}
                  className={styles.actionEdit}
                  title="Edit"
                  onClick={() => openEdit(company)}
                >
                  <IconEdit />
                </button>
                <button
                  data-testid={`CompanySelector-delete-button-${company.id}`}
                  className={styles.actionDelete}
                  title="Delete"
                  onClick={() => { setTarget(company); setDeleteOpen(true); }}
                >
                  <IconTrash />
                </button>
              </div>
            </div>

            {/* chips */}
            <div className={styles.chips}>
              {company.country && (
                <span className={styles.chip}>📍 {company.city}, {company.country}</span>
              )}
              {company.numberOfEmployees != null && (
                <span className={styles.chip}>👥 {company.numberOfEmployees}</span>
              )}
              {company.transactions != null && (
                <span className={styles.chip}>⚡ {company.transactions} tx</span>
              )}
            </div>

            {/* action */}
            <button
              data-testid={`CompanySelector-manage-button-${company.id}`}
              className={styles.manageBtn}
              onClick={() => handleSelect(company)}
            >
              Manage Company <IconArrow />
            </button>
          </div>
        ))}
      </div>

      {/* ── Add Modal ── */}
      <Modal
        open={addOpen}
        onCancel={() => { setAddOpen(false); addForm.resetFields(); }}
        footer={null}
        width={660}
        centered
        destroyOnClose
        closable={false}
        styles={{ body: { padding: 0 }, content: { borderRadius: 14, overflow: "hidden" } }}
        data-testid="CompanySelector-addModal"
      >
        <div className={styles.modalHeader}>
          <span className={styles.modalTitle}>Add New Company</span>
          <button
            data-testid="CompanySelector-addModal-close-button"
            className={styles.modalClose}
            onClick={() => { setAddOpen(false); addForm.resetFields(); }}
          >
            ✕
          </button>
        </div>
        <div className={styles.modalBody}>
          <CompanyForm form={addForm} onFinish={handleAdd} submitting={submitting}
            onCancel={() => { setAddOpen(false); addForm.resetFields(); }} mode="add" />
        </div>
      </Modal>

      {/* ── Edit Modal ── */}
      <Modal
        open={editOpen}
        onCancel={() => { setEditOpen(false); setTarget(null); }}
        footer={null}
        width={660}
        centered
        destroyOnClose
        closable={false}
        styles={{ body: { padding: 0 }, content: { borderRadius: 14, overflow: "hidden" } }}
        data-testid="CompanySelector-editModal"
      >
        <div className={styles.modalHeader}>
          <span className={styles.modalTitle}>Edit — {target?.name ?? ""}</span>
          <button
            data-testid="CompanySelector-editModal-close-button"
            className={styles.modalClose}
            onClick={() => { setEditOpen(false); setTarget(null); }}
          >
            ✕
          </button>
        </div>
        <div className={styles.modalBody}>
          <CompanyForm form={editForm} onFinish={handleEdit} submitting={submitting}
            onCancel={() => { setEditOpen(false); setTarget(null); }} mode="edit" />
        </div>
      </Modal>

      {/* ── Delete Modal ── */}
      <Modal
        open={deleteOpen}
        onCancel={() => { setDeleteOpen(false); setTarget(null); }}
        footer={null} width={400} centered closable={false}
        data-testid="CompanySelector-deleteModal"
      >
        <div className={styles.deleteBody}>
          <div className={styles.deleteIcon}>🗑️</div>
          <h3 className={styles.deleteTitle}>Delete Company?</h3>
          <p className={styles.deleteText}>
            You're about to permanently delete <strong>{target?.name}</strong>.<br />
            All employees and related data will be removed. This cannot be undone.
          </p>
          <div className={styles.deleteFooter}>
            <button
              data-testid="CompanySelector-deleteModal-cancel-button"
              className={styles.btnCancel}
              onClick={() => { setDeleteOpen(false); setTarget(null); }}
            >
              Cancel
            </button>
            <button
              data-testid="CompanySelector-deleteModal-confirm-button"
              className={styles.btnDelete}
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting && <span className={styles.spinner} />}
              Yes, Delete
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}